import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  CardContent, 
  CardMedia, 
  Typography, 
  Grid, 
  Container, 
  IconButton, 
  Box, 
  Button, 
  CardActions, 
  TextField, 
  InputAdornment, 
  CircularProgress, 
  Snackbar, 
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Autocomplete,
  createFilterOptions,
  Divider
} from '@mui/material';
import { ArrowBack, Search, Delete, Add, Close, Edit, CloudDownload, RestartAlt } from '@mui/icons-material';
import ProductEditModal from './ProductEditModal';
import Loader from './Loader';
import { db } from './Dexie';
import { useLiveQuery } from 'dexie-react-hooks';
import genericProduct from '../static/genericProduct.png';
import { log } from '../services/loggingService';
import { syncProducts, refreshProductAttributes, refreshProductCategories, syncProductAttributes } from '../services/productService';

const filter = createFilterOptions();

// Componente Modal per la gestione degli Attributi Prodotto
function ProductAttributesModal({ open, onClose, product, setToast, onBack }) {
  const [attributeName, setAttributeName] = useState(null);
  const [attributeValue, setAttributeValue] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [existingValues, setExistingValues] = useState([]);

  const attributes = useLiveQuery(
    () => product?.id ? db.productAttributes.where('prodotto_id').equals(product.id).toArray() : [],
    [product?.id]
  );

  const allAttributes = useLiveQuery(() => db.attributi.toArray());

  useEffect(() => {
    const fetchValues = async () => {
      if (attributeName && attributeName.id) {
        const values = await db.attributiValori.where('attributo_id').equals(attributeName.id).toArray();
        setExistingValues(values);
      } else {
        setExistingValues([]);
      }
    };
    fetchValues();
  }, [attributeName]);

  const handleRefreshAttributes = async () => {
    if (!product?.id) return;
    setIsRefreshing(true);
    try {
      const result = await refreshProductAttributes(product.id);
      setToast({ open: true, message: result.message, severity: result.success ? 'success' : 'error' });
    } catch (error) {
      setToast({ open: true, message: 'Errore durante lo scarico attributi.', severity: 'error' });
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleResetAttributes = async () => {
    if (!window.confirm('Sei sicuro di voler cancellare TUTTE le associazioni attributi locali e riscaricarle dal server?')) {
      return;
    }

    setIsResetting(true);
    setToast({ open: true, message: 'Reset associazioni in corso...', severity: 'info' });
    
    try {
      const result = await syncProductAttributes();
      setToast({ 
        open: true, 
        message: result.message, 
        severity: result.success ? 'success' : 'error' 
      });
    } catch (error) {
      setToast({ open: true, message: 'Errore durante il reset.', severity: 'error' });
    } finally {
      setIsResetting(false);
    }
  };

  const handleAddAttribute = async () => {
    if (!attributeName || !attributeValue || !product?.id) return;
    setIsSaving(true);

    let finalAttrId = attributeName.id;
    let finalValId = attributeValue.id;

    try {
      if (!finalAttrId) {
        const name = attributeName.inputValue || attributeName.nome || attributeName;
        const response = await fetch('https://deadapi.techysumo.it/techyshop/addAttribute', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': process.env.REACT_APP_API_KEY },
          body: JSON.stringify({ nome: name, obbligatorio: 0 }),
        });
        const result = await response.json();
        if (result.result) {
          finalAttrId = result.details.id || result.details;
          await db.attributi.add({ id: finalAttrId, nome: name, obbligatorio: 0 });
        } else throw new Error("Errore creazione attributo");
      }

      if (!finalValId) {
        const value = attributeValue.inputValue || attributeValue.valore || attributeValue;
        const response = await fetch('https://deadapi.techysumo.it/techyshop/addAttributoValore', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': process.env.REACT_APP_API_KEY },
          body: JSON.stringify({ attributo_id: finalAttrId, valore: value }),
        });
        const result = await response.json();
        if (result.result) {
          finalValId = result.details.id || result.details;
          await db.attributiValori.add({ id: finalValId, attributo_id: finalAttrId, valore: value });
        } else throw new Error("Errore creazione valore");
      }

      const linkResponse = await fetch('https://deadapi.techysumo.it/techyshop/addAttributoProdotto', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': process.env.REACT_APP_API_KEY },
        body: JSON.stringify({ prodotto_id: product.id, attributo_id: finalAttrId, valore_id: finalValId })
      });

      const linkResult = await linkResponse.json();
      if (linkResult.result) {
        await db.productAttributes.add({
          prodotto_id: product.id,
          name: attributeName.inputValue || attributeName.nome || attributeName,
          value: attributeValue.inputValue || attributeValue.valore || attributeValue,
          attributo_id: finalAttrId,
          valore_id: finalValId
        });
        setAttributeName(null);
        setAttributeValue(null);
        setToast({ open: true, message: 'Attributo aggiunto!', severity: 'success' });
      } else throw new Error("Errore collegamento attributo");

    } catch (error) {
      setToast({ open: true, message: `Errore: ${error.message}`, severity: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteAttribute = async (dexieId) => {
    if (!window.confirm("Rimuovere questo attributo dal prodotto?")) return;

    try {
      // 1. Recuperiamo l'attributo da Dexie per avere gli ID necessari all'API
      const attrToRemove = await db.productAttributes.get(dexieId);
      if (!attrToRemove) throw new Error("Attributo non trovato localmente.");

      // 2. Chiamata API per rimuovere l'associazione sul server
      const response = await fetch('https://deadapi.techysumo.it/techyshop/delAttributoProdotto', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': process.env.REACT_APP_API_KEY },
        body: JSON.stringify({ 
          prodotto_id: product.id, 
          attributo_id: attrToRemove.attributo_id,
          valore_id: attrToRemove.valore_id
        })
      });

      const result = await response.json();
      if (result.result) {
        // 3. Se il server conferma, eliminiamo anche da Dexie
        await db.productAttributes.delete(dexieId);
        setToast({ open: true, message: 'Attributo rimosso correttamente.', severity: 'success' });
      } else {
        throw new Error(result.text || "Errore durante la rimozione sul server.");
      }

    } catch (error) {
      setToast({ open: true, message: `Errore: ${error.message}`, severity: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 700 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton edge="start" color="inherit" onClick={onClose} aria-label="close" sx={{ mr: 2 }}>
            <Close />
          </IconButton>
          <Typography variant="h6" component="span">
            Attributi: {product?.nome}
          </Typography>
        </Box>
        <Box>
          <Button 
            startIcon={isRefreshing ? <CircularProgress size={20} color="inherit" /> : <CloudDownload />} 
            variant="contained" 
            color="secondary" 
            onClick={handleRefreshAttributes} 
            disabled={isRefreshing}
            sx={{ mr: 2 }}
          >
            Scarica
          </Button>
          <Button startIcon={<Edit />} variant="outlined" onClick={onBack}>Torna al prodotto</Button>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Container maxWidth="md" sx={{ py: 2 }}>
          <Typography variant="subtitle2" color="primary" gutterBottom>Aggiungi Nuovo Attributo</Typography>
          <Box sx={{ mb: 4, display: 'flex', gap: 2, flexDirection: 'column' }}>
            <Autocomplete
              value={attributeName}
              onChange={(e, v) => setAttributeName(v)}
              options={allAttributes || []}
              getOptionLabel={(o) => o.nome || o.inputValue || ''}
              renderInput={(p) => <TextField {...p} label="Nome Attributo" />}
              freeSolo
              selectOnFocus
              clearOnBlur
              filterOptions={(options, params) => {
                const filtered = filter(options, params);
                if (params.inputValue !== '') {
                  filtered.push({ inputValue: params.inputValue, nome: `Aggiungi "${params.inputValue}"` });
                }
                return filtered;
              }}
            />
            <Autocomplete
              value={attributeValue}
              onChange={(e, v) => setAttributeValue(v)}
              options={existingValues}
              getOptionLabel={(o) => o.valore || o.inputValue || ''}
              renderInput={(p) => <TextField {...p} label="Valore" />}
              freeSolo
              disabled={!attributeName}
              filterOptions={(options, params) => {
                const filtered = filter(options, params);
                if (params.inputValue !== '') {
                  filtered.push({ inputValue: params.inputValue, valore: `Aggiungi "${params.inputValue}"` });
                }
                return filtered;
              }}
            />
            <Button 
              variant="contained" 
              size="large"
              startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <Add />}
              onClick={handleAddAttribute}
              disabled={!attributeName || !attributeValue || isSaving}
            >
              Aggiungi Associazione
            </Button>
          </Box>
          
          <Divider sx={{ my: 4 }} />
          
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>Attributi Assegnati</Typography>
          <List>
            {attributes?.map((attr) => (
              <ListItem key={attr.id} divider secondaryAction={
                <IconButton edge="end" color="error" onClick={() => handleDeleteAttribute(attr.id)}><Delete /></IconButton>
              }>
                <ListItemText 
                  primary={attr.name} 
                  secondary={attr.value} 
                  primaryTypographyProps={{ sx: { fontWeight: 600, color: 'primary.main' } }}
                />
              </ListItem>
            ))}
            {attributes?.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4, opacity: 0.5 }}>
                <Typography>Nessun attributo assegnato a questo prodotto.</Typography>
              </Box>
            )}
          </List>
        </Container>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onBack} variant="text">Torna al Prodotto</Button>
        <Button onClick={onClose} variant="outlined">Chiudi</Button>
      </DialogActions>
    </Dialog>
  );
}

// Componente Modal per la gestione delle Categorie Prodotto
function ProductCategoriesModal({ open, onClose, product, setToast, onBack }) {
  const [selectedCat, setSelectedCat] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const allCategories = useLiveQuery(() => db.categories.toArray());

  const assignedCategories = useLiveQuery(
    async () => {
      if (!product?.id) return [];
      const associations = await db.techyshop_prodotto_categoria
        .where('prodotto_id').equals(parseInt(product.id))
        .toArray();
      const catIds = associations.map(a => a.categoria_id);
      return await db.categories.where('id').anyOf(catIds).toArray();
    },
    [product?.id]
  ) || [];

  const handleRefreshCategories = async () => {
    if (!product?.id) return;
    setIsRefreshing(true);
    try {
      const result = await refreshProductCategories(product.id);
      setToast({ open: true, message: result.message, severity: result.success ? 'success' : 'error' });
    } catch (error) {
      setToast({ open: true, message: 'Errore durante lo scarico categorie.', severity: 'error' });
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleAddCategory = async () => {
    if (!selectedCat || !product?.id) return;
    setIsSaving(true);
    try {
      const response = await fetch('https://deadapi.techysumo.it/techyshop/addCategoriaProdotto', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': process.env.REACT_APP_API_KEY },
        body: JSON.stringify({ prodotto_id: product.id, categoria_id: selectedCat.id }),
      });
      const result = await response.json();
      if (result.result) {
        await db.techyshop_prodotto_categoria.put({ 
          prodotto_id: parseInt(product.id), 
          categoria_id: parseInt(selectedCat.id) 
        });
        setToast({ open: true, message: 'Categoria assegnata!', severity: 'success' });
        setSelectedCat(null);
      } else throw new Error(result.text || "Errore associazione categoria");
    } catch (error) {
      setToast({ open: true, message: `Errore: ${error.message}`, severity: 'error' });
    } finally { setIsSaving(false); }
  };

  const handleDeleteCategory = async (catId) => {
    if (!window.confirm("Rimuovere questa categoria dal prodotto?")) return;
    try {
      const response = await fetch('https://deadapi.techysumo.it/techyshop/delCategoriaProdotto', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': process.env.REACT_APP_API_KEY },
        body: JSON.stringify({ prodotto_id: product.id, categoria_id: catId }),
      });
      const result = await response.json();
      if (result.result) {
        await db.techyshop_prodotto_categoria
          .where({ prodotto_id: parseInt(product.id), categoria_id: parseInt(catId) })
          .delete();
        setToast({ open: true, message: 'Categoria rimossa.', severity: 'info' });
      }
    } catch (error) { setToast({ open: true, message: 'Errore durante la rimozione.', severity: 'error' }); }
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 700 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton edge="start" color="inherit" onClick={onClose} aria-label="close" sx={{ mr: 2 }}>
            <Close />
          </IconButton>
          <Typography variant="h6" component="span">
            Categorie: {product?.nome}
          </Typography>
        </Box>
        <Box>
          <Button 
            startIcon={isRefreshing ? <CircularProgress size={20} color="inherit" /> : <CloudDownload />} 
            variant="contained" 
            color="secondary" 
            onClick={handleRefreshCategories} 
            disabled={isRefreshing}
            sx={{ mr: 2 }}
          >
            Scarica
          </Button>
          <Button startIcon={<Edit />} variant="outlined" onClick={onBack}>Torna al prodotto</Button>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Container maxWidth="md" sx={{ py: 2 }}>
          <Typography variant="subtitle2" color="primary" gutterBottom>Assegna Nuova Categoria</Typography>
          <Box sx={{ mb: 4, display: 'flex', gap: 2 }}>
            <Autocomplete
              sx={{ flex: 1 }}
              value={selectedCat}
              onChange={(e, v) => setSelectedCat(v)}
              options={allCategories || []}
              getOptionLabel={(o) => o.nome || ''}
              renderInput={(p) => <TextField {...p} label="Seleziona Categoria" />}
            />
            <Button 
              variant="contained" 
              onClick={handleAddCategory}
              disabled={!selectedCat || isSaving}
              sx={{ px: 4 }}
            >
              Aggiungi
            </Button>
          </Box>
          
          <Divider sx={{ my: 4 }} />
          
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>Categorie Assegnate</Typography>
          <List>
            {assignedCategories.map((cat) => (
              <ListItem key={cat.id} divider secondaryAction={
                <IconButton edge="end" color="error" onClick={() => handleDeleteCategory(cat.id)}><Delete /></IconButton>
              }>
                <ListItemText 
                  primary={cat.nome} 
                  primaryTypographyProps={{ sx: { fontWeight: 600, color: 'primary.main' } }}
                />
              </ListItem>
            ))}
            {assignedCategories.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4, opacity: 0.5 }}>
                <Typography>Nessuna categoria assegnata a questo prodotto.</Typography>
              </Box>
            )}
          </List>
        </Container>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onBack} variant="text">Torna al Prodotto</Button>
        <Button onClick={onClose} variant="outlined">Chiudi</Button>
      </DialogActions>
    </Dialog>
  );
}

function TechyShopProduct() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAttributesModalOpen, setIsAttributesModalOpen] = useState(false);
  const [isCategoriesModalOpen, setIsCategoriesModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const hasCheckedRef = useRef(false);

  const handleToastClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setToast({ ...toast, open: false });
  };

  const products = useLiveQuery(() => db.products.toArray(), []);

  useEffect(() => {
    if (hasCheckedRef.current || products === undefined) return;
    hasCheckedRef.current = true;

    const checkAndLoadProducts = async () => {
      const productCount = products.length;
      const lastUpdate = await db.genericTable.get({ table: 'product', field: 'lastupdateproduct' });
      const now = new Date();
      let needsUpdate = false;

      if (productCount === 0) needsUpdate = true;
      else if (lastUpdate && lastUpdate.date) {
        const lastDate = new Date(lastUpdate.date);
        if ((now - lastDate) / (1000 * 60 * 60 * 24) >= 1) needsUpdate = true;
      } else needsUpdate = true;
      
      if (needsUpdate) {
        setToast({ open: true, message: "Verifica nuovi prodotti...", severity: 'info' });
        const result = await syncProducts(false);
        setToast({ open: true, message: result.message, severity: result.success ? 'success' : 'error' });
      }
    };
    checkAndLoadProducts();
  }, [products]);

  const filteredProducts = useMemo(() => {
    if (!products) return [];
    const q = searchQuery.toLowerCase();
    return products.filter(p => 
      (p.id?.toString() || '').includes(q) ||
      (p.nome || '').toLowerCase().includes(q) ||
      (p.sku || '').toLowerCase().includes(q) ||
      (p.slug || '').toLowerCase().includes(q)
    );
  }, [searchQuery, products]);

  const handleEditClick = (product) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
  };

  const handleManageAttributes = (product) => {
    setSelectedProduct(product);
    setIsModalOpen(false);
    setTimeout(() => setIsAttributesModalOpen(true), 100);
  };

  const handleManageCategories = (product) => {
    setSelectedProduct(product);
    setIsModalOpen(false);
    setTimeout(() => setIsCategoriesModalOpen(true), 100);
  };

  const handleBackToProduct = () => {
    setIsAttributesModalOpen(false);
    setIsCategoriesModalOpen(false);
    setTimeout(() => setIsModalOpen(true), 100);
  };

  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async (productToSave) => {
    setIsSaving(true);
    let success = false;
    try {
      if (productToSave.id) {
        await db.products.put(productToSave);
        setToast({ open: true, message: 'Prodotto aggiornato!', severity: 'success' });
        success = true;
      } else {
        const response = await fetch('https://deadapi.techysumo.it/techyshop/addProdotto', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': process.env.REACT_APP_API_KEY },
          body: JSON.stringify(productToSave),
        });
        const result = await response.json();
        if (result.result) {
          const newId = result.details?.post || result.details?.id || result.details || Date.now();
          await db.products.add({ ...productToSave, id: newId });
          setToast({ open: true, message: 'Prodotto creato!', severity: 'success' });
          success = true;
        } else throw new Error(result.text || "Errore API.");
      }
    } catch (error) {
      log("Errore salvataggio prodotto:", 'ERROR', error);
      setToast({ open: true, message: `Errore: ${error.message}`, severity: 'error' });
    }
    setIsSaving(false);
    if (success) { setIsModalOpen(false); setSelectedProduct(null); }
  };

  if (!products) return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><Loader /></Box>;

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <IconButton onClick={() => navigate('/techyshop')}><ArrowBack /></IconButton>
        <Typography variant="h4" sx={{ ml: 2, flexGrow: 1 }}>Catalogo Prodotti</Typography>
        <Button variant="contained" color="primary" onClick={() => { setSelectedProduct({ nome: '', prezzo: '', imageUrl: '' }); setIsModalOpen(true); }}>Crea Nuovo</Button>
      </Box>

      <TextField
        fullWidth
        sx={{ mb: 4 }}
        label="Cerca prodotto..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
      />

      <Grid container spacing={4}>
        {filteredProducts.map((p) => (
          <Grid item key={p.id} xs={12} sm={6} md={4}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardMedia component="img" height="200" image={genericProduct} alt={p.nome} />
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography variant="caption" sx={{ opacity: 0.6 }}>ID: {p.id}</Typography>
                <Typography gutterBottom variant="h5">{p.nome}</Typography>
                <Typography variant="body2" color="text.secondary">{p.descrizione_breve}</Typography>
                <Typography variant="h6" color="primary" sx={{ mt: 2 }}>€ {p.prezzo}</Typography>
              </CardContent>
              <CardActions sx={{ justifyContent: 'center', p: 2 }}>
                <Button size="small" variant="contained" fullWidth onClick={() => handleEditClick(p)}>Modifica</Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
      
      <ProductEditModal
        open={isModalOpen}
        onClose={() => { setIsModalOpen(false); setSelectedProduct(null); }}
        product={selectedProduct}
        onSave={handleSave}
        isSaving={isSaving}
        onManageAttributes={handleManageAttributes}
        onManageCategories={handleManageCategories}
      />

      <ProductAttributesModal 
        open={isAttributesModalOpen}
        onClose={() => setIsAttributesModalOpen(false)}
        product={selectedProduct}
        setToast={setToast}
        onBack={handleBackToProduct}
      />

      <ProductCategoriesModal 
        open={isCategoriesModalOpen}
        onClose={() => setIsCategoriesModalOpen(false)}
        product={selectedProduct}
        setToast={setToast}
        onBack={handleBackToProduct}
      />

      <Snackbar open={toast.open} autoHideDuration={6000} onClose={handleToastClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleToastClose} severity={toast.severity}>{toast.message}</Alert>
      </Snackbar>
    </Container>
  );
}

export default TechyShopProduct;
